import React, { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import { Input, useToast } from '@chakra-ui/react';
import { HiInboxArrowDown } from 'react-icons/hi2';
import { strNomalize } from '../../helpers';
import { CirlceIcon, NormalIcon } from '../utils/Icon';
import { useActions } from '../../redux/store';

const UploadComponent = ({
  multiple,
  onUpload,
  disabled,
  label,
  bgColor,
  color,
}: {
  multiple?: boolean;
  onUpload?: (file: FileList) => void;
  disabled?: boolean;
  label?: string;
  bgColor?: string;
  color?: string;
}) => {
  const toast = useToast();

  const { setRowData, setModalType, setModalData } = useActions();

  const [file, setFile] = useState<File | FileList | null>(null);
  const [data, setData] = useState<any[] | null>(null);

  const fileInputRef = React.useRef<any>(null);

  const handleButtonClick = () => {
    setFile(null);
    fileInputRef.current.value = '';
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      setFile(multiple ? event.target.files : event.target.files[0]);
    }
  };

  const reader = new FileReader();
  reader.onload = (event) => {
    const binaryStr = event.target?.result;

    // Conversion du fichier Excel en objet binaire
    const workbook = XLSX.read(binaryStr, { type: 'binary' });

    // Sélectionner la première feuille de calcul
    const sheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[sheetName];

    // Convertir la feuille en JSON
    const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

    // Effectuer un prétraitement ici (par exemple, filtrer certaines données, les transformer, etc.)
    // const preProcessedData = preProcessData(jsonData);

    // Mettre à jour l'état avec les données prétraitées
    setData(jsonData.splice(0, 2));
  };

  useEffect(() => {
    if (multiple) {
      file && onUpload?.(file as FileList);
    } else {
      file && reader.readAsArrayBuffer(file as File);
    }
  }, [file]);

  useEffect(() => {
    if (!data) return;

    setModalData({ data, file });

    if (!multiple) {
      const headers = data[0] as string[];

      const duplicates = headers.filter(
        (item, index) => headers.indexOf(item) !== index,
      );

      if (duplicates.length) {
        toast({
          title: `Le fichier a des colonnes en doublons (${[
            ...new Set(duplicates),
          ].join(', ')})`,
          status: 'error',
        });
        return;
      }

      setRowData(
        headers?.reduce(
          (prev: Record<string, string>, header: string) => ({
            ...prev,
            [strNomalize(header)]: header,
          }),
          {},
        ),
      );

      setModalType('computeBeforeImport');
    }
  }, [data]);

  const IconComponent = bgColor ? CirlceIcon : NormalIcon;

  return (
    <>
      <Input
        isDisabled={disabled}
        ref={fileInputRef}
        type='file'
        onChange={handleFileChange}
        display='none'
        multiple={!!multiple}
      />
      <IconComponent
        icon={<HiInboxArrowDown size='24px' />}
        onClick={disabled ? () => {} : handleButtonClick}
        label={label}
        bgColor={bgColor}
        color={color}
        maxW='fit-content'
      />
    </>
  );
};

export default UploadComponent;
